import type { Identifier } from 'ra-core'

import { isBulkEditAutoAcceptMode, isPlainObjectRecord } from './helpers'
import type { BulkEditDialogOpenPayload, BulkEditListQuery, BulkEditResourceDefinition } from './types'

function isBulkEditId(value: unknown): value is Identifier {
  return typeof value === 'string' || typeof value === 'number'
}

function isPositiveInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1
}

function isListSortOrder(value: unknown): value is 'ASC' | 'DESC' {
  return value === 'ASC' || value === 'DESC'
}

function isBulkEditListQuery(value: unknown): value is BulkEditListQuery {
  if (!isPlainObjectRecord(value)) return false
  if (!isPlainObjectRecord(value.filter)) return false

  if (value.sort !== undefined) {
    if (!isPlainObjectRecord(value.sort)) return false
    const { field, order } = value.sort
    if (typeof field !== 'string' || field.length === 0) return false
    if (!isListSortOrder(order)) return false
  }

  if (value.perPage !== undefined && !isPositiveInteger(value.perPage)) return false
  if (value.maxRecords !== undefined && !isPositiveInteger(value.maxRecords)) return false

  return true
}

function isVisibleFieldKeysValid(definition: BulkEditResourceDefinition, keys: unknown): boolean {
  if (keys === undefined) return true
  if (!Array.isArray(keys) || !keys.every((key): key is string => typeof key === 'string')) {
    return false
  }
  const allowedKeys = definition.allowedBulkFormFieldKeys
  if (!allowedKeys?.length) {
    return keys.every((key) => key.length > 0)
  }
  const allowedSet = new Set(allowedKeys)
  return keys.every((key) => allowedSet.has(key))
}

export function normalizeBulkEditDialogOpenPayload(value: unknown): BulkEditDialogOpenPayload | null {
  if (!isPlainObjectRecord(value)) return null
  if (typeof value.resource !== 'string' || value.resource.length === 0) return null

  const next: Record<string, unknown> = { ...value }
  delete next.autoAccept
  delete next.autoAcceptConfirm
  const raw = value.autoAcceptMode
  if (raw !== undefined && !isBulkEditAutoAcceptMode(raw)) return null
  if (raw === undefined) {
    delete next.autoAcceptMode
  } else {
    next.autoAcceptMode = raw
  }

  return next as BulkEditDialogOpenPayload
}

function validateBulkEditDialogOpenPayloadNormalized(
  value: BulkEditDialogOpenPayload,
  definition: BulkEditResourceDefinition,
  resource: string
): boolean {
  if (value.resource !== resource) return false

  const selectedIds = Array.isArray(value.selectedIds) ? value.selectedIds : undefined
  if (selectedIds !== undefined && !selectedIds.every(isBulkEditId)) {
    return false
  }

  const hasSelectedIds = (selectedIds?.length ?? 0) > 0
  const listQuery = value.listQuery
  const hasValidListQuery = listQuery !== undefined && isBulkEditListQuery(listQuery)

  if (!hasSelectedIds && !hasValidListQuery) return false

  if (value.initialValues !== undefined && !isPlainObjectRecord(value.initialValues)) return false

  if (!isVisibleFieldKeysValid(definition, value.visibleFieldKeys)) return false

  if (value.autoAcceptMode !== undefined && !isBulkEditAutoAcceptMode(value.autoAcceptMode)) {
    return false
  }

  return true
}

export function coerceBulkEditDialogOpenPayload(
  value: unknown,
  definition: BulkEditResourceDefinition,
  resource: string
): BulkEditDialogOpenPayload | null {
  const normalized = normalizeBulkEditDialogOpenPayload(value)
  if (!normalized) return null
  if (!validateBulkEditDialogOpenPayloadNormalized(normalized, definition, resource)) return null
  return normalized
}
